/**
 * Eid gift tags — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_ramadan_eid`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const eidGiftTagsPack: ToilePack = {
    slug: 'eid-gift-tags',
    category: 'season',
    name: 'Eid gift tags',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'classic', label: 'Classic tag', subject: 'a sheet of six luggage-shaped tags in a two-by-three grid, each lettered "Eid Mubarak" with a crescent and star, a punch hole and a blank "To" line'},
        {key: 'kids', label: 'For kids', subject: 'a sheet of round tags lettered "Happy Eid" with a balloon and a small gift drawn on each, a blank name line under the words'},
        {key: 'sweets', label: 'Sweet box', subject: 'a sheet of square tags lettered "Eid Sweets" with a little plate of maamoul and baklava drawn on each and a blank "From" line'},
    ],
    styles: [
        {key: 'outline', label: 'Outline', style: 'bold black outlines on empty shapes, the drawings left to colour in', palette: 'black ink only', framing: 'The pieces spaced apart so each can be cut cleanly'},
        {key: 'geometric', label: 'Geometric', style: 'flat geometric print of eight-pointed stars and crescents with solid colour fills and crisp edges', palette: 'teal, gold and white', framing: 'The coloured pieces spaced apart on the white page'},
        {key: 'pastel', label: 'Pastel', style: 'soft flat illustration with rounded shapes and a gentle dotted border on every piece', palette: 'mint, blush, butter yellow and lavender', framing: 'The pieces spaced apart on the white page'},
    ],
    directions: ['blue and gold', 'cousins', 'pink theme'],
    directionHint: 'try: blue and gold',
    seo: {
        title: 'Eid gift tags printable: Eid Mubarak, Happy Eid, sweets',
        metaDescription: 'Nine printable Eid gift tags: luggage tags lettered Eid Mubarak, round Happy Eid tags for children and sweet-box tags, to print and cut.',
        h1: 'Eid gift tags: Eid Mubarak, Happy Eid, sweet box',
        tagline: 'Nine print-and-cut sheets, three tag shapes each in outline, geometric and pastel finishes.',
        intro: "Print-and-cut tags for Eid presents and sweet boxes for children, cousins and neighbours. The classic tags are luggage-shaped and lettered Eid Mubarak with a crescent and star. The children's tags are round, with Happy Eid, a balloon and a small gift. The sweet-box tags are square, drawn with maamoul and baklava. Each has a blank line for the name. Type a direction such as blue and gold and the tag is drawn with it.",
        useCases: [
            'presents for children at Eid',
            'sweet boxes for neighbours',
            'gifts for cousins',
            'tags for a family wrapping table',
        ],
        faq: [
            {q: 'What is on the sweet-box tag?', a: 'A small plate of maamoul and baklava, with a blank From line under it.'},
            {q: 'How is a tag attached?', a: 'The classic luggage tags have a punch hole for string and a blank To line.'},
            {q: 'What are the three styles?', a: 'Outline is black ink to colour in, geometric is a flat print with solid colour fills, and pastel is soft flat illustration with a dotted border.'},
            {q: 'Can I ask for a different look?', a: 'Yes. Type a direction such as cousins or pink theme and the tag is drawn with it. That is a request, not a promised result.'},
        ],
    },
}
